import React, { useEffect, useState, useContext } from "react";

import StudentList from "./StudentList";
import appContext from "../../../../context/app/appContext";

import ConsentToggles from "./ConsentToggles";
import UnconfirmedStudentDetails from "./UnconfirmedStudentDetails";
import StudentModeSelector from "./StudentModeSelector";
import StudentModeForm from "./StudentModeForm";
import StudentSupervisionSummary from "./StudentSupervisionSummary";
import NotYetConfirmedCheckbox from "./NotYetConfirmedCheckbox";

const StudentDetails = ({ data, onChange, onJumpToStep }) => {
  const [groupOptions, setGroupOptions] = useState([]);
  const [customStudentOptions, setCustomStudentOptions] = useState([]);
  const { serverGet } = useContext(appContext);
  const studentsState = data.students;
  const mode = studentsState?.type || "studentGroups";

  const updateStudents = (partial) => {
    onChange("students", { ...studentsState, ...partial });
  };

  const removeStudentAndPreserveGroups = (studentId, studentsState) => {
    const { groupStudentsMap, selectedGroups, studentMap, customStudents } =
      studentsState;

    const updatedStudentMap = { ...studentMap };
    const updatedGroupStudentsMap = {};
    const updatedSelectedGroups = [];
    const remainingGroupMemberships = {};

    // Remove from studentMap
    delete updatedStudentMap[studentId];

    // Track which groups still fully exist
    for (const [groupId, studentSet] of Object.entries(groupStudentsMap)) {
      const allStillExist = [...studentSet].every(
        (id) => updatedStudentMap[id]
      );

      if (allStillExist) {
        updatedGroupStudentsMap[groupId] = studentSet;
        const groupMeta = selectedGroups.find((g) => g.value === groupId);
        if (groupMeta) updatedSelectedGroups.push(groupMeta);

        for (const id of studentSet) {
          if (!remainingGroupMemberships[id]) {
            remainingGroupMemberships[id] = new Set();
          }
          remainingGroupMemberships[id].add(groupId);
        }
      }
    }

    // Normalize customStudents into a Map
    const updatedCustomStudents = new Map();
    customStudents.forEach((s) => {
      const value = typeof s === "string" ? s : s.value;
      const label = typeof s === "string" ? s : s.label;
      if (value && label && value !== studentId) {
        updatedCustomStudents.set(value, { value, label });
      }
    });

    // Add students from broken groups if they aren't in another group
    for (const [groupId, studentSet] of Object.entries(groupStudentsMap)) {
      if (!updatedGroupStudentsMap[groupId]) {
        for (const id of studentSet) {
          if (!remainingGroupMemberships[id]) {
            const s = updatedStudentMap[id];
            if (s?.stud_name) {
              updatedCustomStudents.set(id, {
                value: id,
                label: s.stud_name,
              });
            }
          }
        }
      }
    }

    return {
      studentMap: updatedStudentMap,
      selectedGroups: updatedSelectedGroups,
      groupStudentsMap: updatedGroupStudentsMap,
      customStudents: Array.from(updatedCustomStudents.values()),
    };
  };

  const sortByYearLevel = (a, b) => {
    const extractYear = (label) => {
      if (/prep/i.test(label)) return 0;
      const match = label.match(/year\s?(\d{1,2})/i);
      return match ? parseInt(match[1]) : Infinity;
    };

    const yearA = extractYear(a.label);
    const yearB = extractYear(b.label);

    if (yearA !== yearB) return yearA - yearB;
    return a.label.localeCompare(b.label);
  };

  // Fetch student groups and custom students
  useEffect(() => {
    const fetchGroupOptions = async () => {
      try {
        const res = await serverGet("/school/studentGroups");
        const rawGroups = res.studentGroups;

        const buckets = {
          whole_school: [],
          cohort: [],
          house: [],
          subject: [],
          class: [],
          activity: [],
        };

        rawGroups.forEach((group) => {
          const type = group.type || "other";
          let label = group.name;

          if (type === "house") {
            const parts = group.id.split("_");
            const year = parts[2];
            const houseName = group.name.split(" - ")[1] || group.name;
            label =
              year && !isNaN(parseInt(year))
                ? `${houseName} (Year ${year})`
                : `Whole House - ${houseName}`;
          }

          if (type === "class") {
            const match = group.name.match(/Class - (\w+)\s+(\w+)\s+\((\d+)\)/);
            if (match) {
              const [, subjectCode, classCode, year] = match;
              label = `Class ${subjectCode}.${classCode} (${year})`;
            }
          }

          buckets[type] = buckets[type] || [];
          buckets[type].push({ value: group.id, label, type });
        });

        const groupedOptions = Object.entries(buckets)
          .filter(([_, arr]) => arr.length > 0)
          .map(([type, options]) => ({
            label: type[0].toUpperCase() + type.slice(1),
            options: options.sort(sortByYearLevel),
          }));

        setGroupOptions(groupedOptions);
      } catch (error) {
        console.error("Failed to fetch group options:", error);
      }
    };

    const fetchCustomStudentOptions = async () => {
      try {
        const res = await serverGet("/school/allStudents");
        const selectedIds = new Set(Object.keys(studentsState.studentMap));
        const options = res.students
          .filter((s) => !selectedIds.has(s.stud_code))
          .map((s) => ({
            value: s.stCode,
            label: `${s.stSurname}, ${s.stPrfName} (Yr ${s.stYrLvl}, ${s.stHmrm})`,
            student: s,
          }));
        setCustomStudentOptions(options);
      } catch (err) {
        console.error("Failed to fetch custom students:", err);
      }
    };

    fetchGroupOptions();
    fetchCustomStudentOptions();
    // eslint-disable-next-line
  }, [studentsState.studentMap]);

  return (
    <div className="space-y-6">
      {/* 🟢 First question: Are students attending? */}
      <div style={{ marginBottom: "-10px" }} className="text-xl">
        Are students attending?
      </div>
      <div className="flex gap-6 text-sm text-gray-800">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            checked={!studentsState.noStudentsAttending}
            onChange={() => {
              updateStudents({ noStudentsAttending: false });
            }}
          />
          Yes, students are attending
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            checked={studentsState.noStudentsAttending}
            onChange={() => {
              updateStudents({
                noStudentsAttending: true,
                notYetConfirmed: false,
                selectedGroups: [],
                groupStudentsMap: {},
                studentMap: {},
                customStudents: [],
              });
              onChange("parentConsentRequired", false);
              onChange("rollsMarked", false);
            }}
          />
          No students attending
        </label>
      </div>

      {/* 🔴 If no students attending, stop here */}
      {studentsState.noStudentsAttending ? null : (
        <>
          {/* 🟡 Student details unknown? */}
          <NotYetConfirmedCheckbox
            studentsState={studentsState}
            updateStudents={updateStudents}
          />

          {/* 🟠 If students are not yet confirmed, show estimate fields */}
          {studentsState.notYetConfirmed && (
            <UnconfirmedStudentDetails
              notYetConfirmedDetails={studentsState.notYetConfirmedDetails}
              onUpdate={updateStudents}
            />
          )}

          {/* 🟢 If students are confirmed, show mode + selectors */}
          {!studentsState.notYetConfirmed && (
            <>
              <StudentModeSelector
                studentsState={studentsState}
                onUpdate={updateStudents}
              />
              <StudentModeForm
                mode={mode}
                studentsState={studentsState}
                groupOptions={groupOptions}
                customStudentOptions={customStudentOptions}
                serverGet={serverGet}
                updateStudents={updateStudents}
              />
            </>
          )}

          {/* 🧾 Consent and rolls marked always appear if students are attending */}
          <ConsentToggles data={data} onChange={onChange} />

          {/* ✅ Only show StudentList if students are confirmed and attending */}
          {!studentsState.notYetConfirmed && (
            <StudentList
              studentMap={studentsState.studentMap}
              groupStudentsMap={studentsState.groupStudentsMap}
              selectedGroups={studentsState.selectedGroups}
              customStudents={studentsState.customStudents}
              showSummaryOnly={false}
              onRemoveStudent={(stud_code) => {
                const updated = removeStudentAndPreserveGroups(
                  stud_code,
                  studentsState
                );
                updateStudents(updated);
              }}
              onClearAll={() =>
                updateStudents({
                  studentMap: {},
                  customStudents: [],
                  groupStudentsMap: {},
                  selectedGroups: [],
                })
              }
            />
          )}

          {/* ✅ Always show summary if students are attending */}
          <StudentSupervisionSummary
            data={data}
            studentsState={studentsState}
            onJumpToStep={onJumpToStep}
          />
        </>
      )}
    </div>
  );
};

export default StudentDetails;
